import React from 'react';
import { Modal, Button, Input, Select, Form } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';

interface FieldEditModalProps {
  open: boolean;
  editingFieldIdx: number | null;
  // When true, we're editing multiple fields at once. Name input is disabled and unchanged.
  bulkMode?: boolean;
  bulkCount?: number;
  modalFieldName: string;
  modalFieldType: string;
  modalRequired: boolean;
  modalComponent: string;
  modalOptionsInput: string;
  modalFontSize: number;
  modalTextAlign: 'left' | 'center' | 'right';
  modalOptions: { value: string; label: string }[];
  onOk: () => void;
  onCancel: () => void;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRequiredChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFieldTypeChange: (value: string) => void;
  onModalComponentChange: (value: string) => void;
  onOptionsInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onFontSizeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTextAlignChange: (value: 'left' | 'center' | 'right') => void;
  onDeleteField: (idx: number) => void;
  onSetFieldDefault?: (name: string, value: any) => void;
}

const FieldEditModal: React.FC<FieldEditModalProps> = ({
  open,
  editingFieldIdx,
  bulkMode = false,
  bulkCount = 0,
  modalFieldName,
  modalFieldType,
  modalRequired,
  modalComponent,
  modalOptionsInput,
  modalFontSize,
  modalTextAlign,
  modalOptions,
  onOk,
  onCancel,
  onInputChange,
  onRequiredChange,
  onFieldTypeChange,
  onModalComponentChange,
  onOptionsInputChange,
  onFontSizeChange,
  onTextAlignChange,
  onDeleteField,
  onSetFieldDefault,
}) => {
  return (
    <Modal
      title={bulkMode ? `Edit Properties (${bulkCount} selected)` : 'Edit Field'}
      open={open}
      onOk={onOk}
      onCancel={onCancel}
      okText="Save"
      centered
      destroyOnClose
      maskClosable
      getContainer={() => document.body}
      width={420}
      style={{ maxWidth: '90vw' }}
      footer={
        <Button type="primary" onClick={onOk}>
          Save
        </Button>
      }
    >
      {!bulkMode ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Input
            value={modalFieldName}
            onChange={onInputChange}
            onPressEnter={onOk}
            autoFocus
            placeholder="Field name"
            maxLength={64}
            style={{ marginBottom: 12, flex: 1 }}
          />
          {editingFieldIdx !== null && (
            <DeleteOutlined
              title="Delete field"
              style={{ color: '#ff4d4f', fontSize: 20, cursor: 'pointer', marginBottom: 12 }}
              onClick={() => onDeleteField(editingFieldIdx)}
            />
          )}
        </div>
      ) : (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
            Enter a base name. First selected field will use it, others will be auto-named as base_1, base_2, ...
          </div>
          <Input
            value={modalFieldName}
            onChange={onInputChange}
            onPressEnter={onOk}
            placeholder="Base field name"
            maxLength={64}
          />
        </div>
      )}
      
      <Select
        value={modalFieldType}
        onChange={onFieldTypeChange}
        style={{ width: '100%', marginBottom: 12 }}
        options={[
          { value: 'text', label: 'Text' },
          { value: 'textarea', label: 'Textarea' },
          { value: 'select', label: 'Select' },
          { value: 'dropdown', label: 'Dropdown (inline)' },
          { value: 'radio', label: 'Radio (inline, horizontal)' },
          { value: 'date', label: 'Date' },
          { value: 'checkbox', label: 'Checkbox' },
          { value: 'signature', label: 'Signature' },
          { value: 'submitted_by', label: 'Submitted By' },
          { value: 'number', label: 'Number' },
        ]}
      />
      
      {modalFieldType !== 'radio' && modalFieldType !== 'dropdown' && (
        <Form.Item label="Associate with modal form" style={{ marginBottom: 12 }}>
          <Select
            value={modalComponent}
            onChange={onModalComponentChange}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) => {
              const label = typeof option?.label === 'string' ? option.label : String(option?.label ?? '');
              const value = typeof option?.value === 'string' ? option.value : String(option?.value ?? '');
              const q = input.toLowerCase();
              return label.toLowerCase().includes(q) || value.toLowerCase().includes(q);
            }}
            options={modalOptions}
            placeholder="Associate with modal form"
          />
        </Form.Item>
      )}

      {modalFieldType === 'submitted_by' && (
        <Form.Item label="Display mode" style={{ marginBottom: 12 }}>
          <Select
            style={{ width: '100%' }}
            placeholder="Select display mode"
            options={[
              { value: 'two_line', label: 'Two lines (Name Surname + Date)' },
              { value: 'single_line', label: 'Single line (Name Surname — Date)' },
            ]}
            // Use modalFontSize as independent state; defaultValue is stored on the field via callback
            // We cannot read the actual current defaultValue here without extra props; default to two_line on change flow
            onChange={(val) => {
              if (onSetFieldDefault) {
                onSetFieldDefault(modalFieldName, val);
              }
            }}
          />
        </Form.Item>
      )}
      
      {(modalFieldType === 'dropdown' ||
        (modalFieldType === 'select' && (
          modalComponent === 'CustomOptionsModal' ||
          modalComponent === 'InlineModal' ||
          modalComponent === 'CustomManualModal' // legacy support
        )) ||
        modalFieldType === 'radio') && (
        <Form.Item label="Options (one per line)">
          <Input.TextArea
            value={modalOptionsInput}
            onChange={onOptionsInputChange}
            autoSize={{ minRows: 2, maxRows: 6 }}
            placeholder={
              modalFieldType === 'radio'
                ? "Enter one option per line. Use value|label (e.g. yes|Yes)"
                : 'Enter each option on a new line'
            }
          />
        </Form.Item>
      )}
      
      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          type="checkbox"
          checked={modalRequired}
          onChange={onRequiredChange}
          style={{ marginRight: 8 }}
        />
        Required
      </label>
      
      <Form.Item label="Font Size" style={{ margin: '8px 0 0 0' }}>
        <Input
          type="number"
          min={8}
          max={48}
          step={1}
          value={modalFontSize}
          onChange={onFontSizeChange}
          style={{ width: 120 }}
          placeholder="Font size (px)"
        />
      </Form.Item>
      <Form.Item label="Value alignment" style={{ margin: '8px 0 0 0' }}>
        <Select
          value={modalTextAlign}
          onChange={onTextAlignChange}
          style={{ width: 160 }}
          options={[
            { value: 'left', label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'right', label: 'Right' },
          ]}
        />
      </Form.Item>
    </Modal>
  );
};

export default FieldEditModal;